import React, { useEffect, useState, useId } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Car, Store, Shield, ArrowRight, Layers, Loader2, UtensilsCrossed, ImageIcon, CheckCircle2, XCircle, MessageCircle, Eye, Clock, AlertCircle, Inbox, Check, X, FileText, BarChart3, Receipt } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/ui/Modal';
import { AnimatePresence, motion } from 'framer-motion';
import { useIdleLogout } from '../../lib/useIdleLogout';
interface RestaurantApplication {
  id: string;
  owner_name: string;
  restaurant_name: string;
  whatsapp: string;
  social_handle: string | null;
  business_type: string;
  uses_whatsapp_orders: boolean;
  has_digital_menu: boolean;
  main_goal: string | null;
  status: 'pending' | 'contacted' | 'approved' | 'rejected';
  created_at: string;
}
const ENROLLMENT_OPTIONS = [{
  title: 'Conductor',
  description: 'Registrar conductor, crear o reutilizar vehículo y dejar accesos listos',
  route: '/admin/drivers/new',
  icon: Car,
  accent: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  iconBg: 'bg-blue-500/15',
  btnClass: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
}, {
  title: 'Subir restaurante',
  description: 'Registrar restaurante, crear acceso inicial y dejarlo listo para operar',
  route: '/admin/enrollment/restaurant',
  icon: Store,
  accent: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  iconBg: 'bg-purple-500/15',
  btnClass: 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
}, {
  title: 'Generar Recibo',
  description: 'Crear recibos de pago profesionales para clientes, imprimir o compartir por WhatsApp',
  route: '/admin/enrollment/receipts',
  icon: Receipt,
  accent: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  iconBg: 'bg-emerald-500/15',
  btnClass: 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
}, {
  title: 'Admin de NOIZA',
  description: 'Crear usuario interno de NOIZA y asignar permisos de plataforma',
  route: '/admin/enrollment/platform-admin',
  icon: Shield,
  accent: 'bg-[#006847]/10 border-[#006847]/20 text-[#00a870]',
  iconBg: 'bg-[#006847]/20',
  btnClass: 'bg-[#006847]/20 text-[#00a870] hover:bg-[#006847]/30'
}, {
  title: 'Actualizar menú',
  description: 'Agregar categorías, productos u opciones a un restaurante existente sin recrearlo',
  route: '/admin/enrollment/menu-update',
  icon: UtensilsCrossed,
  accent: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  iconBg: 'bg-amber-500/15',
  btnClass: 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30',
  btnLabel: 'Iniciar actualización'
}, {
  title: 'Subir imágenes del menú',
  description: 'Relaciona imágenes con productos del menú usando el nombre del archivo',
  route: '/admin/enrollment/menu-images',
  icon: ImageIcon,
  accent: 'bg-teal-500/10 border-teal-500/20 text-teal-400',
  iconBg: 'bg-teal-500/15',
  btnClass: 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30',
  btnLabel: 'Abrir módulo'
}, {
  title: 'Métricas NOIZA',
  description: 'Revisa métricas globales, crecimiento y operación de NOIZA',
  route: '/admin/enrollment/metrics',
  icon: BarChart3,
  accent: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
  iconBg: 'bg-rose-500/15',
  btnClass: 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30',
  btnLabel: 'Ver métricas'
}];
export function EnrollmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // --- Access Guard State ---
  const [guardState, setGuardState] = useState<'loading' | 'authorized' | 'redirecting'>('loading');
  // --- Idle Logout ---
  const {
    showWarning: showIdleWarning
  } = useIdleLogout({
    enabled: guardState === 'authorized',
    timeoutMs: 900000,
    warningBeforeMs: 60000,
    redirectTo: '/admin/login'
  });
  // --- Applications State ---
  const [applications, setApplications] = useState<RestaurantApplication[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'contacted' | 'approved' | 'rejected'>('all');
  const [selectedApp, setSelectedApp] = useState<RestaurantApplication | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [appsModalOpen, setAppsModalOpen] = useState(false);
  const fetchApplications = async () => {
    setAppsLoading(true);
    setAppsError(null);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurant_applications').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setApplications(data || []);
    } catch (err: any) {
      console.error('Error fetching applications:', err);
      setAppsError(err.message);
    } finally {
      setAppsLoading(false);
    }
  };
  // ─── Access Guard ─────────────────────────────────────────────
  useEffect(() => {
    async function guard() {
      try {
        // 1. Check authenticated user
        const {
          data: {
            user
          },
          error: userError
        } = await supabase.auth.getUser();
        if (userError || !user) {
          console.log('[EnrollmentPage] No authenticated user — redirecting to /admin/login');
          setGuardState('redirecting');
          navigate('/admin/login', {
            state: {
              from: location.pathname
            },
            replace: true
          });
          return;
        }
        // 2. Check role in restaurant_users
        const {
          data: roleData,
          error: roleError
        } = await supabase.from('restaurant_users').select('role').eq('user_id', user.id).eq('is_active', true).in('role', ['super_admin', 'platform_admin']).limit(1).maybeSingle();
        if (roleError) {
          console.error('[EnrollmentPage] Role check error:', roleError);
          setGuardState('redirecting');
          navigate('/', {
            replace: true
          });
          return;
        }
        if (!roleData) {
          console.log('[EnrollmentPage] User lacks required role — redirecting to /');
          setGuardState('redirecting');
          navigate('/', {
            replace: true
          });
          return;
        }
        console.log('[EnrollmentPage] Access granted:', {
          email: user.email,
          role: roleData.role
        });
        setGuardState('authorized');
      } catch (err) {
        console.error('[EnrollmentPage] Guard error:', err);
        setGuardState('redirecting');
        navigate('/admin/login', {
          replace: true
        });
      }
    }
    guard();
  }, [navigate, location.pathname]);
  useEffect(() => {
    if (guardState === 'authorized') {
      fetchApplications();
    }
  }, [guardState]);
  const updateStatus = async (id: string, newStatus: 'pending' | 'contacted' | 'approved' | 'rejected') => {
    setUpdatingId(id);
    try {
      const {
        error
      } = await supabase.from('restaurant_applications').update({
        status: newStatus
      }).eq('id', id);
      if (error) throw error;
      setToast({
        message: 'Estado actualizado correctamente',
        type: 'success'
      });
      await fetchApplications();
      if (selectedApp && selectedApp.id === id) {
        setSelectedApp({
          ...selectedApp,
          status: newStatus
        });
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      setToast({
        message: 'Error al actualizar estado',
        type: 'error'
      });
    } finally {
      setUpdatingId(null);
      setTimeout(() => setToast(null), 3000);
    }
  };
  const handleWhatsApp = (whatsapp: string) => {
    const cleanNumber = whatsapp.replace(/\D/g, '');
    if (cleanNumber) {
      window.open(`https://wa.me/${cleanNumber}`, '_blank');
    }
  };
  const filteredApps = applications.filter(app => statusFilter === 'all' || app.status === statusFilter);
  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    contacted: applications.filter(a => a.status === 'contacted').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20" data-id="element-2285">
            Pendiente
          </span>;
      case 'contacted':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/10 text-blue-500 border border-blue-500/20" data-id="element-2286">
            Contactado
          </span>;
      case 'approved':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" data-id="element-2287">
            Aprobado
          </span>;
      case 'rejected':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-500 border border-red-500/20" data-id="element-2288">
            Rechazado
          </span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-500/10 text-gray-400 border border-gray-500/20" data-id="element-2289">
            {status}
          </span>;
    }
  };
  if (guardState !== 'authorized') {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-2290">
        <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-2291" />
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-2292">
      {/* Idle Warning Banner */}
      <AnimatePresence data-id="element-2293">
        {showIdleWarning && <motion.div initial={{
        y: -60,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -60,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed top-0 left-0 right-0 z-[70] flex items-center justify-center px-4 py-3 bg-amber-500/90 text-[#0B0B0B]" data-id="element-2294">
            <Clock className="w-4 h-4 mr-2 shrink-0" data-id="element-2295" />
            <span className="text-sm font-bold" data-id="element-2296">
              Por seguridad, tu sesión se cerrará pronto por inactividad.
            </span>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-10" data-id="element-2297">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-2298">
          <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-2299">
            <Layers className="w-7 h-7 text-[#006847]" data-id="element-2300" />
          </div>
          <div data-id="element-2301">
            <h1 className="text-3xl font-black tracking-tight" data-id="element-2302">
              NOIZA Enrollment
            </h1>
            <p className="text-white/40 text-sm mt-1" data-id="element-2303">
              Gestiona los diferentes tipos de registro y acceso dentro de NOIZA
            </p>
          </div>
        </div>

        {/* Question */}
        <p className="text-white/50 text-lg font-medium" data-id="element-2304">
          ¿Qué deseas registrar hoy?
        </p>

        {/* Cards */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2305">
          {ENROLLMENT_OPTIONS.map(opt => {
          const Icon = opt.icon;
          return <div key={opt.route} className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-2306">
                <div className="space-y-4" data-id="element-2307">
                  <div className={`w-12 h-12 ${opt.iconBg} rounded-xl flex items-center justify-center`} data-id="element-2308">
                    <Icon className="w-6 h-6" data-id="element-2309" />
                  </div>
                  <div data-id="element-2310">
                    <h2 className="text-lg font-bold text-white" data-id="element-2311">
                      {opt.title}
                    </h2>
                    <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-2312">
                      {opt.description}
                    </p>
                  </div>
                </div>

                <button onClick={() => navigate(opt.route)} className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${opt.btnClass}`} data-id="element-2313">
                  {(opt as any).btnLabel || 'Iniciar enrollment'}
                  <ArrowRight className="w-4 h-4" data-id="element-2314" />
                </button>
              </div>;
        })}
        </div>

        {/* ═══ COMPACT APPLICATIONS CARD ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-2315">
          <div className="space-y-4" data-id="element-2316">
            <div className="flex items-start justify-between" data-id="element-2317">
              <div className="w-12 h-12 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-2318">
                <FileText className="w-6 h-6 text-orange-400" data-id="element-2319" />
              </div>
              {stats.pending > 0 && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 animate-pulse" data-id="element-2320">
                  {stats.pending} pendiente{stats.pending !== 1 ? 's' : ''}
                </span>}
            </div>
            <div data-id="element-2321">
              <h2 className="text-lg font-bold text-white" data-id="element-2322">
                Aplicaciones de restaurantes
              </h2>
              <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-2323">
                Revisa las solicitudes de ingreso a NOIZA
              </p>
            </div>

            {/* Compact Stats Row */}
            <div className="flex flex-wrap gap-3 pt-2" data-id="element-2324">
              <div className="flex items-center gap-1.5" data-id="element-2325">
                <div className="w-2 h-2 rounded-full bg-white/30" data-id="element-2326" />
                <span className="text-xs text-white/50" data-id="element-2327">
                  <span className="font-bold text-white" data-id="element-2328">{stats.total}</span>{' '}
                  total
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2329">
                <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-2330" />
                <span className="text-xs text-white/50" data-id="element-2331">
                  <span className="font-bold text-amber-500" data-id="element-2332">
                    {stats.pending}
                  </span>{' '}
                  pendientes
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2333">
                <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-2334" />
                <span className="text-xs text-white/50" data-id="element-2335">
                  <span className="font-bold text-blue-500" data-id="element-2336">
                    {stats.contacted}
                  </span>{' '}
                  contactados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2337">
                <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-2338" />
                <span className="text-xs text-white/50" data-id="element-2339">
                  <span className="font-bold text-emerald-500" data-id="element-2340">
                    {stats.approved}
                  </span>{' '}
                  aprobados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2341">
                <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-2342" />
                <span className="text-xs text-white/50" data-id="element-2343">
                  <span className="font-bold text-red-500" data-id="element-2344">
                    {stats.rejected}
                  </span>{' '}
                  rechazados
                </span>
              </div>
            </div>
          </div>

          <button onClick={() => setAppsModalOpen(true)} className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors bg-orange-500/20 text-orange-400 hover:bg-orange-500/30" data-id="element-2345">
            Ver aplicaciones
            <ArrowRight className="w-4 h-4" data-id="element-2346" />
          </button>
        </div>

        {/* ═══ DOCUMENTOS LEGALES ═══ */}
        <div className="pt-4 border-t border-white/10" data-id="element-2347">
          <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-2348">
            Documentos legales
          </p>
          <div className="flex flex-wrap gap-3" data-id="element-2349">
            <a href="/terminos-restaurantes" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-2350">
              <FileText className="w-4 h-4 text-[#006847]" data-id="element-2351" />
              T&C Restaurantes
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-2352" />
            </a>
            <a href="/terminos-comensales" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-2353">
              <FileText className="w-4 h-4 text-blue-400" data-id="element-2354" />
              T&C Comensales
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-2355" />
            </a>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ═══ FULL-SCREEN APPLICATIONS MANAGER MODAL ═══ */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence data-id="element-2356">
        {appsModalOpen && <motion.div className="fixed inset-0 z-50 flex items-stretch justify-end" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-2357">
            {/* Backdrop */}
            <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAppsModalOpen(false)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2358" />

            {/* Slide-over Panel */}
            <motion.div className="relative w-full sm:w-[640px] lg:w-[780px] h-full bg-[#0B0B0B] border-l border-white/10 shadow-2xl flex flex-col overflow-hidden" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} data-id="element-2359">
              {/* Panel Header */}
              <div className="shrink-0 px-6 py-5 border-b border-white/10 bg-[#0B0B0B]" data-id="element-2360">
                <div className="flex items-center justify-between" data-id="element-2361">
                  <div className="flex items-center gap-3" data-id="element-2362">
                    <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-2363">
                      <FileText className="w-5 h-5 text-orange-400" data-id="element-2364" />
                    </div>
                    <div data-id="element-2365">
                      <h2 className="text-xl font-bold text-white" data-id="element-2366">
                        Aplicaciones de restaurantes
                      </h2>
                      <p className="text-white/40 text-xs mt-0.5" data-id="element-2367">
                        Centro de control de solicitudes
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setAppsModalOpen(false)} className="p-2 -mr-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition-colors" data-id="element-2368">
                    <X className="w-6 h-6" data-id="element-2369" />
                  </button>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-5 gap-3 mt-5" data-id="element-2370">
                  <div className="bg-[#141414] border border-white/10 rounded-xl p-3 text-center" data-id="element-2371">
                    <span className="text-white/40 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-2372">
                      Total
                    </span>
                    <span className="text-xl font-black text-white" data-id="element-2373">
                      {stats.total}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-amber-500/20 rounded-xl p-3 text-center" data-id="element-2374">
                    <span className="text-amber-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-2375">
                      Pendientes
                    </span>
                    <span className="text-xl font-black text-amber-500" data-id="element-2376">
                      {stats.pending}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-blue-500/20 rounded-xl p-3 text-center" data-id="element-2377">
                    <span className="text-blue-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-2378">
                      Contactados
                    </span>
                    <span className="text-xl font-black text-blue-500" data-id="element-2379">
                      {stats.contacted}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-emerald-500/20 rounded-xl p-3 text-center" data-id="element-2380">
                    <span className="text-emerald-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-2381">
                      Aprobados
                    </span>
                    <span className="text-xl font-black text-emerald-500" data-id="element-2382">
                      {stats.approved}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-red-500/20 rounded-xl p-3 text-center" data-id="element-2383">
                    <span className="text-red-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-2384">
                      Rechazados
                    </span>
                    <span className="text-xl font-black text-red-500" data-id="element-2385">
                      {stats.rejected}
                    </span>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex overflow-x-auto hide-scrollbar gap-2 mt-4 pb-1" data-id="element-2386">
                  {[{
                id: 'all',
                label: 'Todos'
              }, {
                id: 'pending',
                label: 'Pendientes'
              }, {
                id: 'contacted',
                label: 'Contactados'
              }, {
                id: 'approved',
                label: 'Aprobados'
              }, {
                id: 'rejected',
                label: 'Rechazados'
              }].map(tab => <button key={tab.id} onClick={() => setStatusFilter(tab.id as any)} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${statusFilter === tab.id ? 'bg-white text-[#0B0B0B]' : 'bg-[#141414] text-white/60 border border-white/10 hover:bg-white/5 hover:text-white'}`} data-id="element-2387">
                      {tab.label}
                    </button>)}
                </div>
              </div>

              {/* Panel Content — Scrollable */}
              <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-2388">
                {appsLoading ? <div className="space-y-4" data-id="element-2389">
                    {[1, 2, 3].map(i => <div key={i} className="h-24 bg-[#141414] rounded-2xl border border-white/5 animate-pulse" data-id="element-2390" />)}
                  </div> : appsError ? <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center" data-id="element-2391">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" data-id="element-2392" />
                    <h3 className="text-lg font-bold text-white mb-1" data-id="element-2393">
                      Error al cargar aplicaciones
                    </h3>
                    <p className="text-white/60 text-sm mb-4" data-id="element-2394">{appsError}</p>
                    <button onClick={fetchApplications} className="px-6 py-2 bg-red-500/20 text-red-400 rounded-xl font-bold text-sm hover:bg-red-500/30 transition-colors" data-id="element-2395">
                      Reintentar
                    </button>
                  </div> : filteredApps.length === 0 ? <div className="bg-[#141414] border border-white/10 rounded-2xl p-12 text-center" data-id="element-2396">
                    <Inbox className="w-12 h-12 text-white/20 mx-auto mb-4" data-id="element-2397" />
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2398">
                      Aún no hay aplicaciones
                    </h3>
                    <p className="text-white/50 text-sm" data-id="element-2399">
                      Cuando un restaurante aplique a NOIZA, aparecerá aquí.
                    </p>
                  </div> : <div className="space-y-4" data-id="element-2400">
                    {filteredApps.map(app => <div key={app.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-colors relative overflow-hidden" data-id="element-2401">
                        {updatingId === app.id && <div className="absolute inset-0 bg-[#141414]/80 backdrop-blur-sm z-10 flex items-center justify-center" data-id="element-2402">
                            <Loader2 className="w-6 h-6 text-white animate-spin" data-id="element-2403" />
                          </div>}

                        <div className="flex flex-col gap-4" data-id="element-2404">
                          {/* Row 1: Name + Status + Date */}
                          <div className="flex items-start justify-between gap-3" data-id="element-2405">
                            <div data-id="element-2406">
                              <div className="flex items-center gap-2 mb-1" data-id="element-2407">
                                <h3 className="font-bold text-lg text-white" data-id="element-2408">
                                  {app.restaurant_name}
                                </h3>
                                {getStatusBadge(app.status)}
                              </div>
                              <p className="text-sm text-white/60" data-id="element-2409">
                                {app.owner_name} • {app.business_type}
                              </p>
                            </div>
                            <p className="text-xs text-white/40 shrink-0" data-id="element-2410">
                              {new Date(app.created_at).toLocaleDateString('es-CR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                            </p>
                          </div>

                          {/* Row 2: Quick Info */}
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-id="element-2411">
                            <div className="flex items-center gap-2" data-id="element-2412">
                              <span className="text-white/40" data-id="element-2413">WhatsApp:</span>
                              <span className="text-white font-medium" data-id="element-2414">
                                {app.whatsapp}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-2415">
                              <span className="text-white/40" data-id="element-2416">
                                Menú digital:
                              </span>
                              <span className="text-white font-medium" data-id="element-2417">
                                {app.has_digital_menu ? 'Sí' : 'No'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-2418">
                              <span className="text-white/40" data-id="element-2419">Pedidos WA:</span>
                              <span className="text-white font-medium" data-id="element-2420">
                                {app.uses_whatsapp_orders ? 'Sí' : 'No'}
                              </span>
                            </div>
                          </div>

                          {/* Row 3: Actions */}
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5" data-id="element-2421">
                            <button onClick={() => {
                      setSelectedApp(app);
                      setDetailOpen(true);
                    }} className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-1.5" data-id="element-2422">
                              <Eye className="w-4 h-4" data-id="element-2423" /> Ver detalle
                            </button>
                            <button onClick={() => handleWhatsApp(app.whatsapp)} disabled={!app.whatsapp} className="px-3 py-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] text-sm font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-1.5" data-id="element-2424">
                              <MessageCircle className="w-4 h-4" data-id="element-2425" /> WhatsApp
                            </button>

                            <div className="flex-1" data-id="element-2426" />

                            {app.status === 'pending' && <button onClick={() => updateStatus(app.id, 'contacted')} className="px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-sm font-bold rounded-xl transition-colors" data-id="element-2427">
                                Contactado
                              </button>}
                            {app.status !== 'approved' && <button onClick={() => updateStatus(app.id, 'approved')} className="px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-sm font-bold rounded-xl transition-colors" data-id="element-2428">
                                Aprobar
                              </button>}
                            {app.status !== 'rejected' && <button onClick={() => updateStatus(app.id, 'rejected')} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-bold rounded-xl transition-colors" data-id="element-2429">
                                Rechazar
                              </button>}
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* ═══ DETAIL MODAL ═══ */}
      {selectedApp && <Modal isOpen={detailOpen} onClose={() => setDetailOpen(false)} title="Detalle de Aplicación" data-id="element-2430">
          <div className="space-y-6" data-id="element-2431">
            <div className="flex items-center justify-between border-b border-white/10 pb-4" data-id="element-2432">
              <div data-id="element-2433">
                <h3 className="text-2xl font-bold text-white" data-id="element-2434">
                  {selectedApp.restaurant_name}
                </h3>
                <p className="text-white/60" data-id="element-2435">{selectedApp.owner_name}</p>
              </div>
              {getStatusBadge(selectedApp.status)}
            </div>

            <div className="grid grid-cols-2 gap-6" data-id="element-2436">
              <div data-id="element-2437">
                <p className="text-white/40 text-sm mb-1" data-id="element-2438">WhatsApp</p>
                <p className="text-white font-medium" data-id="element-2439">{selectedApp.whatsapp}</p>
              </div>
              <div data-id="element-2440">
                <p className="text-white/40 text-sm mb-1" data-id="element-2441">Instagram/Facebook</p>
                <p className="text-white font-medium" data-id="element-2442">
                  {selectedApp.social_handle || '-'}
                </p>
              </div>
              <div data-id="element-2443">
                <p className="text-white/40 text-sm mb-1" data-id="element-2444">Tipo de negocio</p>
                <p className="text-white font-medium" data-id="element-2445">
                  {selectedApp.business_type}
                </p>
              </div>
              <div data-id="element-2446">
                <p className="text-white/40 text-sm mb-1" data-id="element-2447">Fecha de solicitud</p>
                <p className="text-white font-medium" data-id="element-2448">
                  {new Date(selectedApp.created_at).toLocaleString('es-CR')}
                </p>
              </div>
              <div data-id="element-2449">
                <p className="text-white/40 text-sm mb-1" data-id="element-2450">
                  ¿Usa pedidos por WhatsApp?
                </p>
                <p className="text-white font-medium" data-id="element-2451">
                  {selectedApp.uses_whatsapp_orders ? 'Sí' : 'No'}
                </p>
              </div>
              <div data-id="element-2452">
                <p className="text-white/40 text-sm mb-1" data-id="element-2453">
                  ¿Tiene menú digital?
                </p>
                <p className="text-white font-medium" data-id="element-2454">
                  {selectedApp.has_digital_menu ? 'Sí' : 'No'}
                </p>
              </div>
            </div>

            <div data-id="element-2455">
              <p className="text-white/40 text-sm mb-1" data-id="element-2456">Objetivo principal</p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-id="element-2457">
                <p className="text-white/90" data-id="element-2458">
                  {selectedApp.main_goal || 'No especificado'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10" data-id="element-2459">
              <button onClick={() => handleWhatsApp(selectedApp.whatsapp)} className="flex-1 py-3 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors" data-id="element-2460">
                <MessageCircle className="w-5 h-5" data-id="element-2461" /> WhatsApp
              </button>

              {selectedApp.status === 'pending' && <button onClick={() => updateStatus(selectedApp.id, 'contacted')} className="flex-1 py-3 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-xl font-bold transition-colors" data-id="element-2462">
                  Marcar Contactado
                </button>}
              {selectedApp.status !== 'approved' && <button onClick={() => updateStatus(selectedApp.id, 'approved')} className="flex-1 py-3 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-xl font-bold transition-colors" data-id="element-2463">
                  Aprobar
                </button>}
              {selectedApp.status !== 'rejected' && <button onClick={() => updateStatus(selectedApp.id, 'rejected')} className="flex-1 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl font-bold transition-colors" data-id="element-2464">
                  Rechazar
                </button>}
            </div>
          </div>
        </Modal>}

      {/* Toast Notification */}
      {toast && <div className="fixed bottom-6 right-6 z-[60] animate-in slide-in-from-bottom-5 fade-in duration-300" data-id="element-2465">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border ${toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'} backdrop-blur-md`} data-id="element-2466">
            {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5" data-id="element-2467" /> : <AlertCircle className="w-5 h-5" data-id="element-2468" />}
            <span className="font-bold text-sm" data-id="element-2469">{toast.message}</span>
          </div>
        </div>}
    </div>;
}